import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject, input, output, signal } from '@angular/core';
import { ControlValueAccessor, FormsModule, NG_VALUE_ACCESSOR, ReactiveFormsModule } from '@angular/forms';
import { TranslocoDirective, TranslocoService } from '@jsverse/transloco';
import { Media } from '@pixacare/domain';

import { Size } from '@shared/models/enums/size.enum';
import { formatFileSize } from '@shared/utils/format-file-size';
import { TuiIcon } from '@taiga-ui/core';
import { TuiFileLike, TuiFiles } from '@taiga-ui/kit';
import { SequenceMediaComponent } from '../../sequence/sequence-media/sequence-media.component';
import { DropzoneContentComponent } from '../dropzone-content/dropzone-content.component';
import { FileMediaPreviewComponent } from '../file-media-preview/file-media-preview.component';
import { ImageOverlayComponent } from '../image-overlay/image-overlay.component';

const noop = (): void => undefined;

@Component({
  selector: 'pxc-image-input',
  imports: [
    DropzoneContentComponent,
    CommonModule,
    TuiFiles,
    FormsModule,
    ReactiveFormsModule,
    FileMediaPreviewComponent,
    TuiIcon,
    ImageOverlayComponent,
    SequenceMediaComponent,
    TranslocoDirective,
  ],
  templateUrl: './image-input.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: ImageInputComponent,
    },
  ],
  host: {
    class: 'grid',
    '[class.grid-cols-3]': 'size() === Size.LARGE',
    '[class.grid-cols-5]': 'size() === Size.MEDIUM',
    '[class.grid-cols-7]': 'size() === Size.SMALL',
    '[class.gap-4]': 'size() === Size.LARGE',
    '[class.gap-3]': 'size() === Size.MEDIUM',
    '[class.gap-2]': 'size() === Size.SMALL',
  },
})
export class ImageInputComponent implements ControlValueAccessor {
  protected readonly Size = Size;

  // Optional override; when unset the drop-zone text is derived from isLongVideoEnabled below.
  readonly label = input<string>();
  readonly isLongVideoEnabled = input(false);
  readonly accept = input('image/*');
  // Mirrors Taiga's tuiInputFiles default so existing image-only usages keep the same
  // gate; callers that accept large files (e.g. videos) raise it.
  readonly maxFileSize = input(30 * 1024 * 1024);
  protected readonly fileTooLargeMessage = computed(() =>
    this.translocoService.translate('Fichier trop volumineux ({{maxSize}} maximum)', {
      maxSize: formatFileSize(this.maxFileSize()),
    }),
  );
  readonly showDelete = input(true);
  readonly readOnly = input(false);
  readonly size = input<Size>(Size.MEDIUM);
  readonly uploadMode = input<'inline' | 'custom'>('inline');

  readonly medias = input<Media[]>([]);

  readonly mediaDelete = output<number>();
  readonly mediaClickedIdx = output<number>();
  readonly addClicked = output<void>();

  protected readonly disabled = signal(false);
  protected readonly files = signal<File[]>([]);
  protected readonly rejectedFiles = signal<readonly TuiFileLike[]>([]);
  protected readonly hasFiles = computed(
    () => this.files().length > 0 || this.rejectedFiles().length > 0 || this.medias().length > 0,
  );

  private readonly translocoService = inject(TranslocoService);

  private touched = false;
  private onChange: (value: File[]) => void = noop;
  private onTouched: () => void = noop;

  protected isTooLarge(file: TuiFileLike): boolean {
    return (file.size ?? 0) > this.maxFileSize();
  }

  protected removeMedia(media: Media): void {
    this.mediaDelete.emit(media.id);
  }

  protected onReject(files: TuiFileLike | readonly TuiFileLike[]): void {
    this.rejectedFiles.update((current) => [...current, ...(files as TuiFileLike[])]);
  }

  protected onInputClick(event: MouseEvent): void {
    if (this.uploadMode() !== 'custom') {
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    this.addClicked.emit();
  }

  protected onInputFilesChange(files: File[]): void {
    const rejected = this.rejectedFiles();
    const accepted = files.filter((file) => !rejected.includes(file));
    if (this.uploadMode() === 'custom') {
      if (accepted.length === 0) {
        return;
      }
      const merged = [...this.files(), ...accepted];
      this.files.set(merged);
      this.onChange(merged);
      return;
    }
    this.files.set(accepted);
    this.onChange(accepted);
  }

  protected removeRejected({ name }: TuiFileLike): void {
    this.rejectedFiles.update((rejected) => rejected.filter((r) => r.name !== name));
  }

  protected removeFile(target: File): void {
    this.files.update((files) => files.filter((file) => file !== target));
    this.onChange(this.files());
  }

  protected markAsTouched(): void {
    if (!this.touched) {
      this.onTouched();
      this.touched = true;
    }
  }

  writeValue(obj: File[]): void {
    this.files.set(obj ?? []);
  }

  registerOnChange(fn: (value: File[]) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled.set(isDisabled);
  }
}
